import RichTextMessageEditor, {
  type RichTextMessageEditorChange,
} from '@/components/editor/lexical/RichTextMessageEditor';
import { Box, HStack, IconButton } from '@chakra-ui/react';
import type { BatchAttachmentInfo } from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';
import { Eye, EyeClosed } from 'iconoir-react';
import React, { useCallback, useMemo, useRef, type ReactNode } from 'react';
import type { EmailEditorSnapshot } from './use-email-composer-template-flow';
import { useTranslation } from 'react-i18next';
import type { EmailAttachmentsFieldProps } from './EmailAttachmentsField';
import EmailHtmlPreview from './EmailHtmlPreview';

interface EmailEditorComposerProps {
  resetKey: number;
  availableVariables: AvailableVariable[];
  attachments: BatchAttachmentInfo[];
  isPreviewing: boolean;
  previewHtml: string;
  onBodyChange: (html: string, plainText: string) => void;
  onTogglePreview: () => void;
  onFileSelect: (files: FileList | null) => void;
  onRemoveAttachment: (id: string) => void;
  deletingId: string | null;
  isUploading: boolean;
  /** Lexical JSON seeded into the body on mount (e.g. an applied template). */
  initialContent?: string | null;
  /** Latest Lexical snapshot of the body, for save-as-template. */
  onSnapshotChange?: (snapshot: EmailEditorSnapshot) => void;
  /** Node rendered in the footer across from "insert variable" (e.g. a template picker). */
  footerTemplateSlot?: ReactNode;
  footerCounterSlot?: ReactNode;
  variant?: 'card' | 'document';
  attachmentStatus?: Pick<
    EmailAttachmentsFieldProps,
    'issues' | 'onDismissIssue' | 'validationError' | 'maxTotalBytes'
  >;
}

const EmailEditorComposer: React.FC<EmailEditorComposerProps> = React.memo(
  ({
    resetKey,
    availableVariables,
    attachments,
    isPreviewing,
    previewHtml,
    onBodyChange,
    onTogglePreview,
    onFileSelect,
    onRemoveAttachment,
    deletingId,
    isUploading,
    initialContent,
    onSnapshotChange,
    footerTemplateSlot,
    footerCounterSlot,
    variant = 'card',
    attachmentStatus,
  }) => {
    const { t } = useTranslation();
    const previousBodyRef = useRef('');

    const handleEditorChange = useCallback(
      ({ editorState, plainText, html }: RichTextMessageEditorChange) => {
        if (typeof html === 'string' && html !== previousBodyRef.current) {
          previousBodyRef.current = html;
          onBodyChange(html, plainText);
        }
        onSnapshotChange?.({
          lexicalJson: JSON.stringify(editorState.toJSON()),
          plainText,
        });
      },
      [onBodyChange, onSnapshotChange]
    );

    const isDocument = variant === 'document';

    const toolbarTrailingItems = useMemo(
      () => (
        <>
          {!isDocument && (
            <>
              <Box flex={1} />
              <IconButton
                aria-label={t('bulkEmail.compose.preview')}
                size="xs"
                w={6}
                h={6}
                minW={6}
                p={0}
                variant={isPreviewing ? 'solid' : 'ghost'}
                colorPalette="gray"
                rounded="sm"
                onClick={onTogglePreview}
                mr={1}
              >
                {isPreviewing ? (
                  <EyeClosed width={15} height={15} strokeWidth={2} />
                ) : (
                  <Eye width={15} height={15} strokeWidth={2} />
                )}
              </IconButton>
            </>
          )}
        </>
      ),
      [isDocument, isPreviewing, onTogglePreview, t]
    );

    const footerTrailing = useMemo(
      () =>
        footerCounterSlot || footerTemplateSlot ? (
          <HStack gap={3} ml="auto" minW={0}>
            {footerCounterSlot}
            {footerTemplateSlot}
          </HStack>
        ) : undefined,
      [footerCounterSlot, footerTemplateSlot]
    );

    return (
      <RichTextMessageEditor
        editorKey={resetKey}
        namespace="BulkEmailComposer"
        initialContent={initialContent}
        availableVariables={availableVariables}
        onContentChange={handleEditorChange}
        placeholder={t('bulkEmail.compose.body.placeholder')}
        ariaLabel={t('bulkEmail.compose.body.placeholder')}
        insertVariableLabel={t('invitations.editor.insertVariable')}
        translateFn={t}
        variant={variant}
        isPreviewing={isPreviewing}
        includeHtml
        toolbarTrailingItems={toolbarTrailingItems}
        footerTrailing={footerTrailing}
        attachmentControl={{
          attachments,
          onFileSelect,
          onRemoveAttachment,
          deletingId,
          isUploading,
          disabled: isPreviewing,
          ...attachmentStatus,
        }}
        previewContent={
          <EmailHtmlPreview
            html={previewHtml}
            fallbackText={t('bulkEmail.compose.body.placeholder')}
            flex={1}
            px={0}
            py={0}
            minH="200px"
          />
        }
      />
    );
  }
);

EmailEditorComposer.displayName = 'EmailEditorComposer';

export default EmailEditorComposer;
